import appVersion from '@baseDir/appVersion.json';
import posthog from 'posthog-js';

if (process.env.POSTHOG_PROJECT_API_KEY) {
    console.log('Initialize PostHog');
    // set up Posthog
    posthog.init(process.env.POSTHOG_PROJECT_API_KEY, {
        api_host: 'https://activity.iotorq-lean.com',
        // as defined in the Posthog documentation (https://posthog.com/docs/libraries/js#config)
        // since we are using a reverse proxy for the api_host, we need to set the ui_host to the actual Posthog domain
        ui_host: 'https://us.posthog.com',
        autocapture: {
            dom_event_allowlist: ['click'], // DOM events from this list ['click', 'change', 'submit']
            element_allowlist: ['a', 'button'] // DOM elements from this list ['a', 'button', 'form', 'input', 'select', 'textarea', 'label']
        },
        // turns off autocaptured pageviews and ensures you won’t double-capture pageviews on the first load.
        capture_pageview: false,
        // The PostHog Notifications Bar app needs to injects code through posthog-js. Therefore, we need to add the opt-in to enable it.
        opt_in_site_apps: true,
        // Loaded is called once the PostHog scripts have loaded successfully.
        loaded: function (posthog) {
            posthog.register({
                version: appVersion.version
            });
        }
    });
} else {
    console.log(
        'PostHog project API key not set: disabling user activity tracking'
    );
    posthog.disabled = true;
}

/**
 * Identify the user in Posthog
 * @param {User} user
 */
function identify_user(user) {
    if (process.env.POSTHOG_PROJECT_API_KEY && posthog && !posthog.disabled) {
        if (!user.uuid) {
            console.error('User UUID is required to identify user in Posthog');
            return;
        }

        const userData = {
            email: user.email || '',
            username: user.username || '',
            role: user.role || '',
            language: user.language || '',
            job_title: user.job_title || '',
            tenant: user.tenant_uuid || ''
        };
        posthog.identify(user.uuid, userData);

        if (userData.tenant) {
            // Associate the events for this session with a group
            posthog.group('tenant', userData.tenant);
        }
    }
}

/**
 * Check if a feature is enabled in Posthog.
 * @param {String} key - the feature flag key specified in Posthog (e.g. MY_FEATURE_FLAG_ENABLED)
 * @returns {Boolean} - value of the feature flag in Posthog, else false by default
 */
function is_feature_enabled(key) {
    /**
     * IMPORTANT
     * We are moving away from using Posthog feature flags
     * and instead using feature flags in the settings store.
     * For more details, see the API changes here: https://github.com/panevo/lean-api/pull/2711
     */
    if (process.env.POSTHOG_PROJECT_API_KEY && posthog && !posthog.disabled) {
        // isFeatureEnabled could return undefined, so default to false
        // Otherwise, the pipeline tests will throw an error
        return posthog?.isFeatureEnabled(key) ?? false;
    }

    return false;
}

export default ({ app }) => {
    // Set Posthog instance on app
    app.posthog = posthog;
};

export { identify_user, is_feature_enabled, posthog };
